/**
 * Generation: context → direction → patterns → palette → predicates → weighted pick →
 * chord → prose.
 *
 * ## What a considered set of five is
 *
 * The owner's brief for the first three stages: "I want to make sure we think carefully
 * about the direction of harmony, so that we are on a considered journey not just passing
 * from moment to moment." A set is considered when it contains **one continuation of the
 * line already in progress, one release of whatever pressure is standing, one change of
 * scenery that keeps the pressure, and one thing from outside the argument entirely** —
 * and when the cards refer to the same line, so they read as five endings to one sentence
 * rather than five sentences.
 *
 * Four mechanisms, each one level below the last:
 *
 * | slot | what it is | who fills it |
 * |---|---|---|
 * | 0 | the line in progress, completed — or, when nothing is in progress, the anchor | `claimPatternSlots`, else the palette |
 * | 1 | the anchor: the move that simply works | `samplePalette` rule 3, weighted by `shape[0]` |
 * | 2–4 | the shape: a release, a change of scenery, a continuation, one free slot | the palette × `ROLE_FIT` |
 * | any | the uniform slot, drawn from the whole vocabulary, untouched by any of this | `samplePalette` rule 2 |
 *
 * The pattern slot is claimed **before** the palette is sized, which is what keeps every
 * guarantee below intact by construction rather than by repair: its kind and its
 * pitch-class set are registered first, so the palette cannot draw either.
 *
 * ## The quiescence invariant
 *
 * **With no path, this file's output is byte-for-byte what it was before any of the above
 * existed.** `readDirection` reads `open`, whose shape is all `free` and whose every
 * `ROLE_FIT` entry is exactly 1; `matchPatterns` returns `[]`; `claimPatternSlots` returns
 * `[]` without drawing a single rng value. That is what makes the existing suite — almost
 * all of which passes no path — a valid regression net, and it is pinned digest by digest
 * in `generate.test.ts`. (The pin has been regenerated since, deliberately, for changes
 * to pathless sets that were meant to be ones — the root rule and the recolouring weight
 * below among them — and its header records each.)
 *
 * ## The guarantees
 *
 * For any current chord, any key estimate and any adventurousness, a set comes back with
 *
 *  - **`branchCount` suggestions**, or as many as the library can legally supply;
 *  - **at least three distinct movement kinds**, by construction — a kind is used once;
 *  - **no duplicate pitch-class sets**, within the set or against anything the caller
 *    said was already on screen;
 *  - **never the chord already sounding**, which is not a branch;
 *  - **no two suggestions on the same root**, wherever the library can avoid it — which
 *    is what caps the recolourings of the current root (A7, Amaj7 from Am) at one. See
 *    `pickFresh`;
 *  - **legal spellings**: `noteSpellings` always sound exactly the chord's pitch classes.
 *
 * Those are the same guarantees the server's validation layer enforces on model output.
 * The engine meets them by construction rather than by being checked, which is the
 * whole reason it can be trusted without a network.
 *
 * ## Purity
 *
 * No clock, no `Math.random`, no I/O, no DOM. The only entropy is `input.rng`, injected
 * on the signature so that "this is deterministic" is a fact about the type rather than
 * a convention. Ids come from `input.mintId` for the same reason.
 *
 * One import discipline this file and its neighbours keep: everything taken from
 * `../llm/schemas.js` is an `import type`. That module carries Zod, and the client
 * deliberately does not ship Zod in its main bundle — a single value import here would
 * quietly put 78 kB back.
 */

import { noteName, spellPitchClass, type KeyContext } from '../chord/enharmonic.js';
import { pcSetEquals, pcSetKey } from '../chord/pcset.js';
import type { CanonicalChord, PitchClass } from '../chord/types.js';
import { BRANCH_SUGGESTION_LIMITS, type MovementKind } from '../llm/constants.js';
import type { BranchSuggestion } from '../llm/schemas.js';
import { createEmptyPreferenceHints } from '../prefs/types.js';
import type { KeyEstimate } from '../session/types.js';
import { buildTargetChord, chordToneNames, computeFacts, notesOutsideKey } from './build.js';
import { ROLE_FIT, readDirection, roleOf } from './direction.js';
import {
  KEY_SETTLED_CONFIDENCE,
  confidenceFor,
  fillTemplate,
  impliedKeyFor,
  keyName,
  romanNumeralFor,
  slotValues,
} from './explain.js';
import { MOVE_LIBRARY } from './library.js';
import { matchPatterns, readContext } from './match.js';
import { samplePalette } from './palette.js';
import type {
  CurrentQuality,
  DirectionReading,
  HarmonicPattern,
  HeuristicBranch,
  HeuristicBranchInput,
  HeuristicMove,
  PatternMatch,
  SlotRole,
} from './types.js';

/**
 * How many recently played kinds `avoidAfterKinds` looks back over.
 *
 * Two, because the complaint a move like this answers is "you just showed me that" and
 * the memory behind that complaint is short. Longer would start suppressing kinds the
 * player has forgotten choosing.
 */
export const RECENT_KIND_WINDOW = 2;

/** A target that repeats the chord immediately before this one is offered, but reluctantly. */
export const PREVIOUS_CHORD_PENALTY = 0.3;

/** And one that repeats something further back in the path, less reluctantly. */
export const RECENT_PATH_PENALTY = 0.6;

/** How far back in the path a repeat still counts as a repeat. */
export const PATH_MEMORY = 4;

/**
 * When a slot has no in-band candidate, how far past the nearest available band the
 * backfill will reach. Small on purpose: a timid player must not be handed a tritone
 * substitution because the palette happened to draw `planing`.
 */
export const BACKFILL_BAND_SLACK = 0.1;

/**
 * How far past the nearest band the backfill will reach for a root the set does not
 * already have, before it gives up and repeats one.
 *
 * Wider than `BACKFILL_BAND_SLACK` because a second card on one root is a worse set than
 * a card a quarter-step off the slider; narrow enough that the low end still never sees a
 * move whose band starts at 0.6. At 0.1 a repeated root survived in 122 of the 1 728
 * pathless sweep cases; at 0.25, 29, all of them at the two ends of the slider and most of
 * them from an augmented triad, where the library genuinely runs out.
 */
export const ROOT_BAND_SLACK = 0.25;

/** Floor on a candidate's weight, so nothing eligible is ever unreachable. */
const MIN_CANDIDATE_WEIGHT = 0.08;

/**
 * How many cards in one set may be authored by the pattern layer.
 *
 * One. The library's only real gap is the landing; the *other* endings to the same
 * sentence — the deceptive vi, the minor sixth — are already in `MOVE_LIBRARY` and are
 * reached by `boostMoveIds` instead. That gets two endings to one sentence without
 * turning the column into a funnel, and it halves the curated prose. Raising this to two
 * is a one-line change if the offered sets ever read as too thin.
 */
export const PATTERN_SLOT_LIMIT = 1;

/**
 * How many slots the palette must always keep.
 *
 * Three, and it is the uniform escape hatch this protects rather than the count.
 * `samplePalette` draws its unweighted slot only when it is asked for at least two kinds
 * (`rest.length + 1 < count`), so reserving pattern slots *before* the palette is sized
 * preserves rule 2 by construction — provided the palette is never squeezed below this.
 */
export const MIN_PALETTE_SLOTS = 3;

/**
 * How much a matched pattern lifts the library moves it points at.
 *
 * `PERSONALISATION_MAX_MULTIPLIER` exactly, and the pairing is the argument: a pattern's
 * tilt is as strong as a player's learned taste and no stronger. Neither may zero a
 * candidate and neither may make one certain.
 */
export const PATTERN_MOVE_BOOST = 2;

/** And how much it damps one. `PERSONALISATION_MIN_MULTIPLIER`, for the same reason. */
export const PATTERN_MOVE_DAMP = 0.5;

/**
 * How much a candidate on the current chord's own root is weighed down.
 *
 * A recolouring — Am → A, C → Cm, G7 → Gm7 — is a real option and stays one: the root
 * rule lets one through, and this is a weight, never a filter (rule 14). But the library
 * has a recolouring in almost every kind, so with nothing against it one of the five cards
 * was "the same chord, re-lit" in nine conservative sets out of ten, and five cards should
 * be five places to go.
 */
export const SAME_ROOT_PENALTY = 0.4;

interface Candidate {
  move: HeuristicMove;
  target: CanonicalChord;
  weight: number;
  /** How far the requested adventurousness sits outside this move's band. 0 when inside. */
  bandDistance: number;
  /** Set only when a matched pattern authored this candidate. */
  pattern?: HarmonicPattern;
  /** The matched tail's symbols, oldest first, including the current chord. */
  setup?: readonly string[];
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0.5;
  return Math.min(1, Math.max(0, value));
}

function truncate(text: string, limit: number): string {
  return text.length <= limit ? text : `${text.slice(0, limit - 1).trimEnd()}…`;
}

function pick<T>(items: readonly T[], rng: () => number): T {
  const index = Math.min(items.length - 1, Math.max(0, Math.floor(rng() * items.length)));
  return items[index] as T;
}

function bandDistanceOf(move: HeuristicMove, adventurousness: number): number {
  const [lo, hi] = move.band;
  if (adventurousness < lo) return lo - adventurousness;
  if (adventurousness > hi) return adventurousness - hi;
  return 0;
}

/** The centre of a move's band. This is the `adventurousness` the suggestion reports. */
function bandCentre(move: HeuristicMove): number {
  const [lo, hi] = move.band;
  return clamp01((lo + hi) / 2);
}

/**
 * Does this move's *reading* apply to this moment at all?
 *
 * Everything here is a hard yes or no. Soft preferences — how well the move suits the
 * adventurousness, whether its target has been played recently — are weights, applied
 * separately, because a soft preference expressed as a filter is how a library of
 * eighty moves quietly becomes a library of six.
 */
function predicatesHold(
  move: HeuristicMove,
  currentQuality: CurrentQuality,
  keyConfidence: number,
  keyMode: 'major' | 'minor',
  recentKinds: readonly MovementKind[],
): boolean {
  if (move.worksFrom && !move.worksFrom.includes(currentQuality)) return false;

  const needsKey = move.needsKey === true || move.needsKeyMode !== undefined || move.from === 'key';
  if (needsKey && keyConfidence < KEY_SETTLED_CONFIDENCE) return false;
  if (move.needsKeyMode !== undefined && move.needsKeyMode !== keyMode) return false;

  if (move.avoidAfterKinds) {
    for (const kind of recentKinds) {
      if (move.avoidAfterKinds.includes(kind)) return false;
    }
  }

  return true;
}

/**
 * The one predicate that needs the chord as well as the move.
 *
 * `needsDiatonicTarget` is a claim about where a move **goes**, so it cannot be answered
 * before `buildTargetChord` has said where that is — which is why it lives here rather
 * than in `predicatesHold` with the rest. Skipped entirely below `KEY_SETTLED_CONFIDENCE`:
 * a claim the key can contradict is only checked when there is a key to contradict it.
 */
function targetSuitsKey(
  move: HeuristicMove,
  target: CanonicalChord,
  keyEstimate: KeyEstimate,
): boolean {
  if (move.needsDiatonicTarget !== true) return true;
  if (keyEstimate.confidence < KEY_SETTLED_CONFIDENCE) return true;
  return notesOutsideKey(target, keyEstimate).length === 0;
}

interface Workspace {
  input: HeuristicBranchInput;
  currentQuality: CurrentQuality;
  recentKinds: readonly MovementKind[];
  previousKey: string | null;
  pathKeys: ReadonlySet<string>;
  usedSets: Set<string>;
  usedKinds: Set<MovementKind>;
  /**
   * The kinds a pattern claim took, as opposed to the kinds the caller excluded.
   *
   * Kept apart because `backfill` may reconsider one and may never reconsider the other.
   * See the note on `backfill`.
   */
  claimedKinds: Set<MovementKind>;
  /** The roots already offered in this set. See `pickFresh`. */
  usedRoots: Set<PitchClass>;
  direction: DirectionReading;
  /** Library moves the strongest match points at. One author for the tilt, never two. */
  boosted: ReadonlySet<string>;
  damped: ReadonlySet<string>;
}

/** Every move of a kind that could legally be offered right now, with its weight. */
function candidatesFor(
  workspace: Workspace,
  kind: MovementKind | null,
  requireInBand: boolean,
  slotRole: SlotRole,
  reconsiderClaimed = false,
): Candidate[] {
  const { input, currentQuality } = workspace;
  const adventurousness = clamp01(input.adventurousness);
  const out: Candidate[] = [];

  for (const move of MOVE_LIBRARY) {
    if (kind !== null && move.movementKind !== kind) continue;
    if (kind === null && workspace.usedKinds.has(move.movementKind)) {
      if (!reconsiderClaimed || !workspace.claimedKinds.has(move.movementKind)) continue;
    }

    const distance = bandDistanceOf(move, adventurousness);
    if (requireInBand && distance > 0) continue;

    if (
      !predicatesHold(
        move,
        currentQuality,
        input.keyEstimate.confidence,
        input.keyEstimate.mode,
        workspace.recentKinds,
      )
    ) {
      continue;
    }

    const target = buildTargetChord(move, input.current, input.keyEstimate, currentQuality);
    if (!target) continue;
    if (!targetSuitsKey(move, target, input.keyEstimate)) continue;
    if (pcSetEquals(target.pitchClasses, input.current.pitchClasses)) continue;

    const key = pcSetKey(target.pitchClasses);
    if (workspace.usedSets.has(key)) continue;

    out.push({
      move,
      target,
      weight: weightOf(workspace, move, target, key, distance, slotRole),
      bandDistance: distance,
    });
  }

  // Every recolouring of the chord under the hands is one destination, however many ways
  // the library has of colouring it: from Am, A, Amaj7, A6/9 and Asus4 are four cards and
  // one place. Without this the backfill weighed that place four or five times over, and
  // the penalty in `weightOf` was spread too thin to matter.
  const rootPc = input.current.rootPc;
  const recolourings = out.filter((candidate) => candidate.target.rootPc === rootPc).length;
  if (recolourings > 1) {
    for (const candidate of out) {
      if (candidate.target.rootPc === rootPc) candidate.weight /= recolourings;
    }
  }

  return out;
}

function weightOf(
  workspace: Workspace,
  move: HeuristicMove,
  target: CanonicalChord,
  targetKey: string,
  bandDistance: number,
  slotRole: SlotRole,
): number {
  const adventurousness = clamp01(workspace.input.adventurousness);
  const [lo, hi] = move.band;
  // A triangular fit inside the band: a move is at its most likely at the centre of its
  // own range and least likely at its edges, so a set at 0.5 is not simply the union of
  // everything eligible at 0.5.
  const halfWidth = Math.max(0.15, (hi - lo) / 2);
  let weight = Math.max(
    MIN_CANDIDATE_WEIGHT,
    1 - Math.abs(adventurousness - bandCentre(move)) / halfWidth,
  );
  if (bandDistance > 0) weight *= Math.max(MIN_CANDIDATE_WEIGHT, 1 - bandDistance);

  // Offering the chord just left is offering to undo the last move. Not forbidden — a
  // dominant resolving back where it came from is the most ordinary thing in music —
  // but it should lose to a candidate that goes somewhere.
  if (targetKey === workspace.previousKey) weight *= PREVIOUS_CHORD_PENALTY;
  else if (workspace.pathKeys.has(targetKey)) weight *= RECENT_PATH_PENALTY;

  if (target.rootPc === workspace.input.current.rootPc) weight *= SAME_ROOT_PENALTY;

  // The matched pattern's other endings, and the moves it points away from.
  if (workspace.boosted.has(move.id)) weight *= PATTERN_MOVE_BOOST;
  if (workspace.damped.has(move.id)) weight *= PATTERN_MOVE_DAMP;

  // And what this slot is for. Floored and capped, never zero: the direction model may
  // tilt a slot and may not empty one.
  weight *=
    ROLE_FIT[slotRole][roleOf(workspace.input.current, target, workspace.input.keyEstimate)];

  return weight;
}

/**
 * A weighted pick that will not put a second card on a root the set already has.
 *
 * The palette's thirteen kinds are about *how* a chord is reached, not *where*, so it can
 * believe it is offering variety while it offers one destination twice: from Am,
 * `this root, as a dominant` (A7) and `brightened, with a 7th` (Amaj7) are two kinds and
 * one place, and the owner's word for a set like that was "weird". The rule is general
 * — **no two cards on one root**, not merely "one recolouring" — because the same thing
 * happens away from the current chord: A♭ and A♭maj7 from C7, D and Dm, G♯m7 and G♯7
 * were each in about half the pathless sets before this. Five cards are five places to
 * go, which is what "a lot of options" asks for. The cost is that IV beside iv (F and Fm
 * from C) no longer shares a set; each is still offered, just not in the same five, and
 * a regenerate is one tap.
 *
 * It behaves exactly like the duplicate-set rule, as a refusal rather than a weight: a
 * slot whose only candidates share a root falls through to `backfill`. And it is the
 * **weakest** guarantee in the file, deliberately — the count and the adventurousness band
 * both outrank it, so where the only legal candidates left share a root the set is
 * still full rather than one short (`backfill`'s last resort). A shorter set to keep the
 * roots apart would be the engine trading options away to protect a rule about options.
 *
 * Draws over the whole list first and redraws over the fresh ones only when the first
 * draw shares a root. That is distributionally the same as filtering — a rejected draw
 * renormalises over what is left — but it consumes the same rng values as before whenever
 * the first draw is fresh, so a set that never met the rule is byte-for-byte unchanged and
 * the quiescence diff contains only sets that did. Returns `null`, drawing nothing, when
 * every candidate shares a root.
 */
function pickFresh(
  workspace: Workspace,
  candidates: readonly Candidate[],
  rng: () => number,
): Candidate | null {
  const isFresh = (candidate: Candidate): boolean =>
    !workspace.usedRoots.has(candidate.target.rootPc);
  const fresh = candidates.filter(isFresh);
  if (fresh.length === 0) return null;
  const first = weightedPick(candidates, rng);
  return isFresh(first) ? first : weightedPick(fresh, rng);
}

/**
 * Whether a slot whose every candidate recolours the current chord gives way to backfill.
 *
 * `SAME_ROOT_PENALTY` in `weightOf` only bites where a recolouring competes with
 * something, and inside a kind like `parallel` it competes with nothing — every member is
 * on the same root, so the weight scales them all alike and the palette drawing the kind
 * was the whole decision. So the slot itself is weighed: it keeps its recolouring with
 * probability `SAME_ROOT_PENALTY` and otherwise goes to `backfill` with its kind set
 * aside — still reachable, through a recolouring in another kind (A7 is a secondary
 * dominant) or as the backfill's last resort, but no longer the default. Draws an rng
 * value only in that one situation.
 */
function deferRecolouring(
  workspace: Workspace,
  candidates: readonly Candidate[],
  rng: () => number,
): boolean {
  const rootPc = workspace.input.current.rootPc;
  if (!candidates.every((candidate) => candidate.target.rootPc === rootPc)) return false;
  return rng() >= SAME_ROOT_PENALTY;
}

/** Record a chosen card, so nothing after it repeats its chord, its kind or its root. */
function register(workspace: Workspace, candidate: Candidate): void {
  workspace.usedSets.add(pcSetKey(candidate.target.pitchClasses));
  workspace.usedKinds.add(candidate.move.movementKind);
  workspace.usedRoots.add(candidate.target.rootPc);
}

function weightedPick(candidates: readonly Candidate[], rng: () => number): Candidate {
  let total = 0;
  for (const candidate of candidates) total += candidate.weight;
  if (total <= 0) return pick(candidates, rng);

  let threshold = rng() * total;
  for (const candidate of candidates) {
    threshold -= candidate.weight;
    if (threshold <= 0) return candidate;
  }
  return candidates[candidates.length - 1] as Candidate;
}

/**
 * Fill a slot the palette's kind could not.
 *
 * Nearest band wins, and only ties within `BACKFILL_BAND_SLACK` of the nearest compete —
 * a hard cut rather than a weighting, because the promise "a timid setting produces
 * timid options" has to survive the palette drawing a kind that only owns wild ones.
 *
 * **A pattern claim's kind is reconsidered here; a caller's exclusion never is.** The two
 * look alike in `usedKinds` and mean opposite things. `excludeKinds` is the caller saying
 * *that kind is already on screen*, which is a fact this set cannot argue with. A claimed
 * kind is the engine's own bookkeeping, and holding it back cost a suggestion: with
 * `standing-dominant` claiming `functional-dominant` on a target the library's only
 * in-band `functional-subdominant` move would also have produced, the palette's slot fell
 * through as a duplicate set and the backfill then refused the claimed kind, so one claim
 * consumed a slot, a chord **and** a kind — three things where `MIN_PALETTE_SLOTS`
 * reserves against one. The count is a promise ("`branchCount` suggestions, or as many as
 * the library can legally supply") and the library could legally supply them. At most one
 * kind is ever reconsidered, since at most `PATTERN_SLOT_LIMIT` is claimed, so the
 * three-distinct-kinds guarantee has a slot of slack and keeps.
 *
 * **The root rule gives way last, and only here.** A fresh root is looked for inside the
 * band shortlist, then inside the shortlist with a claimed kind reconsidered, then a
 * little further out — `ROOT_BAND_SLACK` rather than `BACKFILL_BAND_SLACK` past the
 * nearest band, which is not far enough to hand a timid setting anything from the wild
 * end — and only when none of those has one does the slot take a shared root rather than
 * come back empty.
 */
function backfill(
  workspace: Workspace,
  rng: () => number,
  allowSharedRoot: boolean,
): Candidate | null {
  // `'free'` on purpose: a slot the palette could not fill is already a compromise, and
  // it does not get a second opinion about what it should have been for.
  const strict = bandShortlist(candidatesFor(workspace, null, false, 'free'));
  const fromStrict = pickFresh(workspace, strict, rng);
  if (fromStrict) return fromStrict;

  const everything = candidatesFor(workspace, null, false, 'free', true);
  const wide = bandShortlist(everything);
  const fromWide = pickFresh(workspace, wide, rng);
  if (fromWide) return fromWide;

  const reach = bandShortlist(everything, ROOT_BAND_SLACK);
  const fromReach = pickFresh(workspace, reach, rng);
  if (fromReach) return fromReach;

  // Every legal candidate left shares a root with a card already chosen. A full set
  // beats a tidy one: see `pickFresh`. The caller decides when it has come to that.
  if (!allowSharedRoot) return null;
  const fallback = strict.length > 0 ? strict : wide;
  return fallback.length > 0 ? weightedPick(fallback, rng) : null;
}

/** Nearest band wins, and only ties within `slack` of it compete. */
function bandShortlist(candidates: readonly Candidate[], slack = BACKFILL_BAND_SLACK): Candidate[] {
  let nearest = Number.POSITIVE_INFINITY;
  for (const candidate of candidates) nearest = Math.min(nearest, candidate.bandDistance);
  return candidates.filter((candidate) => candidate.bandDistance <= nearest + slack);
}

/**
 * How specific a continuation is about the moment it is being offered into.
 *
 * A continuation that names a mode beats one that only needs a settled key, which beats
 * one that needs nothing. This is what makes a V7 land minor in a settled minor key and
 * major everywhere else, without a conditional inside a record.
 */
function specificityOf(move: HeuristicMove): number {
  if (move.needsKeyMode !== undefined) return 2;
  if (move.needsKey === true || move.from === 'key') return 1;
  return 0;
}

/**
 * Claim the pattern slots: the line already in progress, completed.
 *
 * Walks the matches strongest-first and takes at most `PATTERN_SLOT_LIMIT`. **Gathers
 * before it draws** — no rng value is consumed unless there is something to choose
 * between — which is what makes the quiescence invariant absolute rather than probable.
 *
 * A claimed candidate is emitted ahead of the palette's anchor. The anchor is first today
 * so that the first card to stream in is the one that simply works; a completion of the
 * phrase already in progress is more that than the anchor is.
 *
 * Three behaviours fall out of this and need no code of their own. A pattern is **spent**
 * by construction: play something other than the offered landing and the tail no longer
 * ends where the pattern requires, so it stops matching — no stored flag, which also
 * makes it rewind-safe. **Regenerate drops the pattern card**, because the landing's
 * pitch-class set arrives in `excludePitchClassSets` and the claim falls through to the
 * next continuation, or to none. And pattern slots are **exempt from personalisation
 * suppression**, structurally: the most common real journey is ii–V–I–ii–V–I, which is
 * exactly what `personalisationMultiplier` turns away from as `takenKinds` fills with
 * `functional-dominant`, and this bypasses the palette entirely.
 */
function claimPatternSlots(
  workspace: Workspace,
  matches: readonly PatternMatch[],
  limit: number,
  rng: () => number,
): Candidate[] {
  const { input, currentQuality } = workspace;
  const adventurousness = clamp01(input.adventurousness);
  const claimed: Candidate[] = [];

  for (const match of matches) {
    if (claimed.length >= limit) break;

    const eligible: Candidate[] = [];
    for (const move of match.pattern.continuations) {
      if (workspace.usedKinds.has(move.movementKind)) continue;
      if (bandDistanceOf(move, adventurousness) > 0) continue;
      if (
        !predicatesHold(
          move,
          currentQuality,
          input.keyEstimate.confidence,
          input.keyEstimate.mode,
          workspace.recentKinds,
        )
      ) {
        continue;
      }

      const target = buildTargetChord(move, input.current, input.keyEstimate, currentQuality);
      if (!target) continue;
      if (!targetSuitsKey(move, target, input.keyEstimate)) continue;
      if (pcSetEquals(target.pitchClasses, input.current.pitchClasses)) continue;
      const key = pcSetKey(target.pitchClasses);
      if (workspace.usedSets.has(key)) continue;

      eligible.push({
        move,
        target,
        // The pattern has already chosen the role, so `weightOf` is asked for the band
        // fit and the recency penalties alone — `'free'`, and no boost of its own.
        weight: weightOf(workspace, move, target, key, 0, 'free'),
        bandDistance: 0,
        pattern: match.pattern,
        setup: match.setup,
      });
    }

    if (eligible.length === 0) continue;

    const best = Math.max(...eligible.map((candidate) => specificityOf(candidate.move)));
    const shortlist = eligible.filter((candidate) => specificityOf(candidate.move) === best);
    // A claim is the line in progress, so it does not give way to the root rule: when
    // every landing shares a root with something already claimed it still lands.
    const chosen = pickFresh(workspace, shortlist, rng) ?? weightedPick(shortlist, rng);

    claimed.push(chosen);
    register(workspace, chosen);
    workspace.claimedKinds.add(chosen.move.movementKind);
  }

  return claimed;
}

/**
 * Generate a branch set, with the move and its explanation attached to each branch.
 *
 * `mintId` is called once per emitted suggestion, in output order. Ids are the caller's
 * business: CLAUDE.md's rule is that they are minted server-side, and the reason behind
 * that rule is *stability* — a model's own ids are neither stable nor unique across
 * retries. An engine that mints its own would satisfy the reason and break the letter,
 * so it does not mint at all and takes an injection instead.
 */
export function generateHeuristicBranchSet(input: HeuristicBranchInput): HeuristicBranch[] {
  const { rng } = input;
  const context = readContext(input);
  const direction = readDirection(context);
  const matches = matchPatterns(context);
  const strongest = matches[0];

  const path = input.path ?? [];
  const previous = path.length > 0 ? path[path.length - 1] : undefined;

  const workspace: Workspace = {
    input,
    currentQuality: context.currentQuality,
    recentKinds: (input.takenKinds ?? []).slice(-RECENT_KIND_WINDOW),
    previousKey: previous ? pcSetKey(previous.pitchClasses) : null,
    pathKeys: new Set(path.slice(-PATH_MEMORY).map((entry) => pcSetKey(entry.pitchClasses))),
    usedSets: new Set([
      pcSetKey(input.current.pitchClasses),
      ...(input.excludePitchClassSets ?? []).map((pcs) => pcSetKey(pcs)),
    ]),
    usedKinds: new Set(input.excludeKinds ?? []),
    claimedKinds: new Set(),
    // Empty, not seeded with the current root: one recolouring of the chord under the
    // hands is a real option (Am → A7), and it is the second that repeats the first.
    usedRoots: new Set(),
    direction,
    // Only the strongest match tilts the palette, so the tilt has one author. A
    // boost-only pattern applies its tilt and claims nothing.
    boosted: new Set(strongest?.pattern.boostMoveIds ?? []),
    damped: new Set(strongest?.pattern.dampMoveIds ?? []),
  };

  const slotBudget = Math.max(
    0,
    Math.min(PATTERN_SLOT_LIMIT, Math.round(input.branchCount) - MIN_PALETTE_SLOTS),
  );
  const claimed = claimPatternSlots(workspace, matches, slotBudget, rng);

  const paletteExcludeKinds = [
    ...(input.excludeKinds ?? []),
    ...claimed.map((candidate) => candidate.move.movementKind),
  ];

  const palette = samplePalette({
    branchCount: input.branchCount - claimed.length,
    adventurousness: input.adventurousness,
    takenKinds: input.takenKinds ?? [],
    ignoredKinds: input.ignoredKinds ?? [],
    preferenceHints: input.preferenceHints ?? createEmptyPreferenceHints(),
    ...(paletteExcludeKinds.length > 0 ? { excludeKinds: paletteExcludeKinds } : {}),
    random: rng,
  });

  const chosen: Candidate[] = [];
  const unfilled: number[] = [];
  const deferred: MovementKind[] = [];

  for (let slot = 0; slot < palette.length; slot += 1) {
    const kind = palette[slot] as MovementKind;
    const candidates = candidatesFor(workspace, kind, true, direction.shape[slot] ?? 'free');
    // A kind whose only in-band candidates share a root is a duplicate, like a kind whose
    // only candidates are chords already chosen.
    const candidate = pickFresh(workspace, candidates, rng);
    if (!candidate) {
      // Remember the slot rather than dropping it: the count is a promise.
      unfilled.push(chosen.length);
      continue;
    }
    if (deferRecolouring(workspace, candidates, rng)) {
      // The kind sits out the backfill as well, or the backfill simply hands the slot
      // back to the recolouring it just declined. It returns below if nothing else can.
      deferred.push(kind);
      workspace.usedKinds.add(kind);
      unfilled.push(chosen.length);
      continue;
    }
    chosen.push(candidate);
    register(workspace, candidate);
  }

  for (let i = 0; i < unfilled.length; i += 1) {
    // In order of what gives way: a set-aside recolouring before a repeated root, and a
    // repeated root before a short set.
    let candidate = backfill(workspace, rng, false);
    if (!candidate && deferred.length > 0) {
      for (const kind of deferred.splice(0)) workspace.usedKinds.delete(kind);
      candidate = backfill(workspace, rng, false);
    }
    candidate ??= backfill(workspace, rng, true);
    if (!candidate) break;
    chosen.push(candidate);
    register(workspace, candidate);
  }

  const key: KeyContext = { tonicPc: input.keyEstimate.tonicPc, mode: input.keyEstimate.mode };
  const spell = (pc: PitchClass): string => noteName(spellPitchClass(pc, key));
  const keyLabel = keyName(input.keyEstimate, spell(input.keyEstimate.tonicPc));

  return [...claimed, ...chosen].map((candidate) =>
    toBranch(candidate, input, keyLabel, spell, rng),
  );
}

/** The suggestions alone, in the wire shape. Nothing downstream can tell who wrote them. */
export function generateHeuristicBranches(input: HeuristicBranchInput): BranchSuggestion[] {
  return generateHeuristicBranchSet(input).map((branch) => branch.suggestion);
}

function toBranch(
  candidate: Candidate,
  input: HeuristicBranchInput,
  keyLabel: string,
  spell: (pc: PitchClass) => string,
  rng: () => number,
): HeuristicBranch {
  const { move, target } = candidate;
  const facts = computeFacts(input.current, target, input.keyEstimate, candidate.setup);
  // Guaranteed non-null: `buildTargetChord` already refused any chord whose stack of
  // thirds cannot be written, which is the only way this returns null.
  const spellings = chordToneNames(target) ?? [];
  const analysisConfidence = confidenceFor(move, target, input.keyEstimate);

  const suggestion: BranchSuggestion = {
    id: input.mintId(),
    chord: {
      root: target.root,
      quality: target.quality,
      ...(target.seventh ? { seventh: target.seventh } : {}),
      extensions: [...target.extensions],
      alterations: [...target.alterations],
      omissions: [...target.omissions],
      ...(target.bass ? { bass: target.bass } : {}),
    },
    displaySymbol: target.symbol,
    noteSpellings: spellings,
    movementKind: move.movementKind,
    relationLabel: truncate(
      pick(move.phrases.relationLabels, rng),
      BRANCH_SUGGESTION_LIMITS.relationLabel,
    ),
    shortDescription: truncate(
      pick(move.phrases.descriptions, rng),
      BRANCH_SUGGESTION_LIMITS.shortDescription,
    ),
    styleTags: move.phrases.styleTags.slice(0, BRANCH_SUGGESTION_LIMITS.styleTags),
    character: pick(move.phrases.characters, rng),
    adventurousness: bandCentre(move),
    analysisConfidence,
  };

  const numeral = romanNumeralFor(move, target, input.keyEstimate);
  if (numeral !== undefined) suggestion.romanNumeral = numeral;

  const impliedKey = impliedKeyFor(move, target, spell);
  if (impliedKey !== undefined) suggestion.impliedKey = impliedKey;

  if (move.phrases.voicingHint !== undefined) {
    suggestion.voicingHint = truncate(
      move.phrases.voicingHint,
      BRANCH_SUGGESTION_LIMITS.voicingHint,
    );
  }
  if (move.phrases.ambiguityNote !== undefined) {
    suggestion.ambiguityNote = truncate(
      move.phrases.ambiguityNote,
      BRANCH_SUGGESTION_LIMITS.ambiguityNote,
    );
  }

  return {
    suggestion,
    moveId: move.id,
    explanation: fillTemplate(
      move.phrases.explanation,
      slotValues(facts, keyLabel, candidate.pattern?.name),
    ),
    analysisConfidence,
  };
}
